import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Switch } from '@/shared/components/ui/switch';
import { Separator } from '@/shared/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useProjects } from '@/shared/hooks/useProjects';

const ORG_SCOPE_VALUE = '__organization__';

interface FormSettingsPopoverProps {
  emailRequired: boolean;
  onEmailRequiredChange: (value: boolean) => void;
  disabled?: boolean;
  /** Only settable at create time — the backend's UpdateForm has no `projectId` field. */
  projectId?: string;
  onProjectIdChange?: (projectId: string | undefined) => void;
  scopeEditable?: boolean;
}

export function FormSettingsPopover({
  emailRequired,
  onEmailRequiredChange,
  disabled = false,
  projectId,
  onProjectIdChange,
  scopeEditable = false,
}: FormSettingsPopoverProps) {
  const { t } = useTranslation('forms');
  const { data: projects = [] } = useProjects();

  const projectName = projects.find((p) => p.id === projectId)?.name;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('settings.title')}
              data-testid="forms-settings-popover-trigger"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('settings.title')}</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80" align="end" data-testid="forms-settings-popover">
        <p className="text-sm font-semibold mb-3">{t('settings.title')}</p>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-1.5">{t('settings.scope')}</p>
            {scopeEditable ? (
              <Select
                value={projectId ?? ORG_SCOPE_VALUE}
                onValueChange={(v) => onProjectIdChange?.(v === ORG_SCOPE_VALUE ? undefined : v)}
              >
                <SelectTrigger data-testid="forms-settings-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ORG_SCOPE_VALUE}>
                    {t('list.table.scopeOrganization')}
                  </SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id ?? ''} value={project.id ?? ''}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p
                className="text-sm text-muted-foreground"
                data-testid="forms-settings-scope-readonly"
              >
                {projectId ? (projectName ?? projectId) : t('list.table.scopeOrganization')}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {scopeEditable ? t('settings.scopeEditableHint') : t('settings.scopeLockedHint')}
            </p>
          </div>

          <Separator />

          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium">{t('settings.emailRequired')}</p>
              <p className="text-xs text-muted-foreground">
                {t('settings.emailRequiredDescription')}
              </p>
            </div>
            <Switch
              checked={emailRequired}
              disabled={disabled}
              onCheckedChange={onEmailRequiredChange}
              data-testid="forms-settings-email-required-toggle"
            />
          </div>

          <p className="text-xs text-muted-foreground border-t pt-3">
            {t('settings.distributionHint')}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
